import { inject, Injectable } from '@angular/core';
import { Memoize } from '@lx/shared/utils-http';
import { Observable, of } from 'rxjs';
import { QuoteOnlineSchool } from '@lx/quote-tool/shared/types';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader, getConfiguration } from '@lx/shared/tokens';
import { SchoolControllerApiService } from '@lx/shared/api';
import { map, switchMap } from 'rxjs/operators';
import { SchoolV4 } from '@lx/shared/data-access/school';
import { SchoolTypesEnum } from '@lx/shared/data-access/types';
import { SharedDataStore } from '../store';
import { ResidenceDataService } from './residence-data.service';

@Injectable({
  providedIn: 'root',
})
export class OnlineSchoolService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly configuration = getConfiguration();
  private readonly residenceDataService = inject(ResidenceDataService);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly schoolControllerService = inject(SchoolControllerApiService);

  // Fake online location to group online courses by country
  private readonly fakeOnlineLocation = 'ONL';

  @Memoize((countryCode: string) => countryCode)
  findOnlineSchoolsForResidenceCountry(countryCode: string): Observable<QuoteOnlineSchool> {
    return this.schoolControllerService.findOnlineSchools(this.locale, this.companyHeader, countryCode);
  }

  getOnlineGroupedSchoolLocations(language: string) {
    return this.sharedDataStore.schools$.pipe(
      switchMap((schools: SchoolV4[]) => {
        // If there are no schools or the configuration flag to show online courses is not active, return an empty array
        if (!schools?.length || !this.configuration.QUOTE_TOOL_ONLINE_FLOW_ENABLED) {
          return of([]);
        }

        // Get the first online school for the selected language. It is taken the first one because
        // here we only need one to be displayed in country selector, but until we don't know
        // the residence country we won't know which is the correct online school to display.
        const onlineSchoolForSelectedLanguage = schools?.find(
          ({ courseLanguages, isOnline }) => isOnline && courseLanguages[SchoolTypesEnum.ADULT].find((courseLanguage) => courseLanguage === language),
        );

        // If there is no online school for the selected language, return an empty array
        if (!onlineSchoolForSelectedLanguage) {
          return of([]);
        }

        return this.findOnlineSchoolsForResidenceCountry(this.residenceDataService.detectCountryCode()).pipe(
          map((onlineSchools) => {
            const { name } = onlineSchoolForSelectedLanguage;
            return [
              {
                label: $localize`Online`,
                value: this.fakeOnlineLocation,
                items: [
                  {
                    value: onlineSchools.onlineSchoolCodes[0],
                    label: name,
                  },
                ],
              },
            ];
          }),
        );
      }),
    );
  }
}
